import React, { PureComponent } from 'react';
import { ContentLoader } from 'Common/components/Atoms/ContentLoader';
import { Div } from 'Common/components/Atoms/Div';
import { FontIcon } from 'Common/components/Atoms/FontIcon';
import { InputField } from 'Common/components/Atoms/InputField';
import StepCard from '../../../../components/StepCard';
import { InputLabel } from '../../../ReviewerSignUpProfile';
import { Theme } from '@mui/material/styles';
import withTheme, { WithTheme } from '@mui/styles/withTheme';
import { actions as signupsActions } from '../../../../reducers/signups';
import { actions as accountActions } from '../../../../reducers/Account';
import { accountSelector, isLoadingAccountSelector } from 'selectors/Account';
import { connect } from 'react-redux';
import { Action, bindActionCreators, Dispatch } from 'redux';
import { faDollarSign } from '@fortawesome/pro-regular-svg-icons';
import { User } from '../../../../models/User';
import { ADDITIONAL_INFO } from 'DisplayText/ReviewerOnboarding/Application/AdditionalInfo';
import {
  logEvent,
  REVIEWER_APPLICATION_ADDITIONAL_INFO,
} from 'utils/analytics';

interface AddReviewerInfoInput {
  hourlyRate: number;
  yearsDevExperience: number;
  yearsCodeReview: number;
  reviewDescription: string | undefined;
}

export interface Props {
  user: User | null;
  addReviewerInfo: (input: AddReviewerInfoInput) => void;
  isUpdating: boolean;
  isLoadingAccount: boolean;
  theme: Theme;
  error?: string;
  forceNextStep: () => void;
  previousStep: () => void;
}

type Errors = {
  hourlyRate?: boolean;
  yearsDevExperience?: boolean;
  yearsCodeReview?: boolean;
};

interface State {
  errors: Errors;
  hourlyRate?: number;
  yearsDevExperience?: number;
  yearsCodeReview?: number;
  reviewDescription: string;
}

export const MAX_OPTIONAL_TEXT_LENGTH = 1_000;

export class AddtionalInfoComponent extends PureComponent<Props, State> {
  static displayName = 'AdditionalInfo';

  state: State = {
    errors: {},
    hourlyRate: undefined,
    yearsDevExperience: undefined,
    yearsCodeReview: undefined,
    reviewDescription: '',
  };

  componentDidMount() {
    this.setAdditionalInfo();
    logEvent(REVIEWER_APPLICATION_ADDITIONAL_INFO);
  }

  componentDidUpdate(prevProps: Props) {
    if (!prevProps.user && this.props.user) {
      this.setAdditionalInfo();
    }
  }

  private setAdditionalInfo = () => {
    const { additional_info } = this.props.user?.reviewer_profile || {};

    this.setState({
      hourlyRate: additional_info?.hourly_rate,
      yearsDevExperience: additional_info?.years_developing,
      yearsCodeReview: additional_info?.years_reviewing,
      reviewDescription: additional_info?.review_description ?? '',
    });
  };

  private handleChange = (name: keyof Errors, value: string) => {
    const { errors } = this.state;

    this.setState({
      ...this.state,
      [name]: value === '' ? undefined : Number.parseFloat(value),
      errors: { ...errors, [name]: undefined },
    });
  };

  private handleSubmit = () => {
    const {
      hourlyRate,
      yearsDevExperience,
      yearsCodeReview,
      reviewDescription,
    } = this.state;

    if (
      hourlyRate === undefined ||
      yearsDevExperience === undefined ||
      yearsCodeReview === undefined
    ) {
      this.setState({
        errors: {
          hourlyRate: hourlyRate === undefined,
          yearsDevExperience: yearsDevExperience === undefined,
          yearsCodeReview: yearsCodeReview === undefined,
        },
      });
      return;
    }

    const { addReviewerInfo, user, forceNextStep } = this.props;
    const { additional_info } = user?.reviewer_profile || {};
    const {
      hourly_rate,
      years_developing,
      years_reviewing,
      review_description,
    } = additional_info || {};

    const roundedHourlyRate = Number.parseFloat(hourlyRate.toFixed(2));
    const roundedYearsDevExperience = Math.round(yearsDevExperience);
    const roundedYearsCodeReview = Math.round(yearsCodeReview);

    if (
      roundedHourlyRate !== hourly_rate ||
      roundedYearsDevExperience !== years_developing ||
      roundedYearsCodeReview !== years_reviewing ||
      reviewDescription !== review_description
    ) {
      addReviewerInfo({
        hourlyRate: roundedHourlyRate,
        yearsDevExperience: roundedYearsDevExperience,
        yearsCodeReview: roundedYearsCodeReview,
        reviewDescription,
      });
    } else {
      forceNextStep();
    }
  };

  render(): JSX.Element {
    const { isUpdating, previousStep, isLoadingAccount, theme } = this.props;
    const {
      errors,
      hourlyRate,
      yearsDevExperience,
      yearsCodeReview,
      reviewDescription,
    } = this.state;

    return (
      <StepCard
        handleSubmit={this.handleSubmit}
        isUpdating={isUpdating}
        previousStep={previousStep}
      >
        <Div mb={3}>
          <InputLabel>{ADDITIONAL_INFO.HOURLY_RATE_TITLE}</InputLabel>
          {isLoadingAccount ? (
            <ContentLoader height="38px" width="100%" />
          ) : (
            <InputField
              id="hourlyRate"
              data-testid="qa-hourly"
              type="number"
              min="0"
              placeholder="80"
              value={hourlyRate || ''}
              onChange={(event) =>
                this.handleChange('hourlyRate', event.target.value)
              }
              width="100%"
              error={errors?.hourlyRate}
              startIcon={
                <FontIcon
                  icon={faDollarSign}
                  size="sm"
                  color={theme.palette.text.primary}
                />
              }
              helperText={
                errors?.hourlyRate
                  ? ADDITIONAL_INFO.HOURLY_RATE_ERROR
                  : ADDITIONAL_INFO.HOURLY_RATE_HELPTEXT
              }
            />
          )}
        </Div>
        <Div mb={3}>
          <InputLabel>{ADDITIONAL_INFO.DEV_EXPERIENCE_TITLE}</InputLabel>
          {isLoadingAccount ? (
            <ContentLoader height="38px" width="100%" />
          ) : (
            <InputField
              id="yearsDevExperience"
              data-testid="qa-years-developing"
              type="number"
              min="0"
              placeholder="5"
              value={yearsDevExperience || ''}
              onChange={(event) =>
                this.handleChange('yearsDevExperience', event.target.value)
              }
              width="100%"
              error={errors?.yearsDevExperience}
              helperText={
                errors?.yearsDevExperience &&
                ADDITIONAL_INFO.DEV_EXPERIENCE_ERROR
              }
            />
          )}
        </Div>
        <Div mb={3}>
          <InputLabel>{ADDITIONAL_INFO.CR_EXPERIENCE_TITLE}</InputLabel>
          {isLoadingAccount ? (
            <ContentLoader height="38px" width="100%" />
          ) : (
            <InputField
              id="yearsCodeReview"
              data-testid="qa-years-reviewing"
              type="number"
              min="0"
              placeholder="5"
              value={yearsCodeReview || ''}
              onChange={(event) =>
                this.handleChange('yearsCodeReview', event.target.value)
              }
              width="100%"
              error={errors?.yearsCodeReview}
              helperText={
                errors?.yearsCodeReview && ADDITIONAL_INFO.CR_EXPERIENCE_ERROR
              }
            />
          )}
        </Div>
        <Div mb={3}>
          <InputLabel>{ADDITIONAL_INFO.REVIEW_DESCRIPTION_TITLE}</InputLabel>
          {isLoadingAccount ? (
            <ContentLoader height="98px" width="100%" />
          ) : (
            <InputField
              id="reviewDescription"
              data-testid="qa-review-description"
              rows={5}
              multiline={true}
              placeholder={ADDITIONAL_INFO.REVIEW_DESCRIPTION_PLACEHOLDER}
              value={reviewDescription || ''}
              onChange={(event) => {
                this.setState({
                  reviewDescription: event.target.value.substring(
                    0,
                    MAX_OPTIONAL_TEXT_LENGTH
                  ),
                });
              }}
              width="100%"
            />
          )}
        </Div>
      </StepCard>
    );
  }
}

function mapStateToProps(state: any) {
  const { signups } = state;
  const account = accountSelector(state);
  const isLoadingAccount = isLoadingAccountSelector(state);
  return {
    isUpdating: signups.isUpdating,
    isLoadingAccount,
    user: account.user,
    error: signups.error,
  };
}

function mapDispatchToProps(dispatch: Dispatch<Action>) {
  return bindActionCreators({ ...signupsActions, ...accountActions }, dispatch);
}

export default withTheme<Theme, React.JSXElementConstructor<Props>>(
  connect(mapStateToProps, mapDispatchToProps)(AddtionalInfoComponent)
) as any;
